import React, { useState, useEffect } from 'react'
import styles from './ITPage.module.scss'
import { getITServices, getITProjects, createITProject, createITConsultation } from '../../../service/ITService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import ServiceRequestModal from '../../../components/ServiceRequestModal/ServiceRequestModal'
import { ComputerIcon, StarIcon, PeopleIcon, MoneyIcon, CheckIcon, PlusIcon, EditIcon, SearchIcon, SparkleIcon, SettingsIcon, ShieldIcon, ConstructionIcon, LightningIcon } from '../../../components/CustomIcons'
import { useAuth } from '../../../contexts/AuthContext'

const ITPage = ({ embedded = false, goBack }) => {
  const { user } = useAuth()
  const [services, setServices] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [view, setView] = useState('services') // services, projects, consultations, about
  const [selectedProject, setSelectedProject] = useState(null)
  const [showConsultationModal, setShowConsultationModal] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    technology: '',
    price_range: ''
  })
  const [sortBy, setSortBy] = useState('created_at')
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  })
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: ''
  })
  const [consultationType, setConsultationType] = useState('')
  const [searchInputValue, setSearchInputValue] = useState('')

  // Service request modal states
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [selectedService, setSelectedService] = useState(null)
  
  // Notification state
  const [showAuthNotification, setShowAuthNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('') // 'technical', 'expert', 'business'

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentPage, filters])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (view === 'services') {
        await loadServices()
      } else if (view === 'projects') {
        await loadProjects()
      }
    } catch (err) {
      setError(`Ошибка загрузки IT сервисов`)
      setRetryAction(() => loadData)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const loadServices = async () => {
    const response = await getITServices({
      page: currentPage,
      search: filters.search,
      category: filters.category,
      technology: filters.technology,
      ordering: sortBy
    })
    setServices(response.data.results || response.data || [])
    setTotalPages(response.data.total_pages || 1)
  }

  const loadProjects = async () => {
    const response = await getITProjects({
      page: currentPage,
      search: filters.search,
      technology: filters.technology,
      ordering: sortBy
    })
    setProjects(response.data.results || response.data || [])
    setTotalPages(response.data.total_pages || 1)
  }



  const handleServiceOrder = (service) => {
    setSelectedService(service)
    setShowServiceModal(true)
  }
  
  const handleServiceRequestSuccess = (message) => {
    setShowServiceModal(false)
    setSelectedService(null)
    setNotificationMessage(message || 'Заявка успешно отправлена!')
    setShowAuthNotification(true)
    setTimeout(() => setShowAuthNotification(false), 4000)
  }
  
  const handleServiceRequestClose = () => {
    setShowServiceModal(false)
    setSelectedService(null)
  }

  const handleConsultationRequest = async (e) => {
    e.preventDefault()
    try {
      await createITConsultation({
        name: consultationForm.name || (user && user.username) || '',
        email: consultationForm.email || (user && user.email) || '',
        phone: consultationForm.phone || (user && user.phone) || '',
        description: consultationForm.description,
        consultation_type: consultationType || 'other'
      })
      setShowConsultationModal(false)
      setConsultationForm({ name: '', email: '', phone: '', description: '' })
      setConsultationType('')
      setNotificationMessage('Заявка на консультацию отправлена!')
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    } catch (err) {
      console.error('Ошибка запроса консультации:', err)
      setNotificationMessage(`Ошибка: ${err.response?.data?.detail || err.message || 'Попробуйте позже'}`)
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    }
  }

  const handleProjectRequest = async (e) => {
    e.preventDefault()
    try {
      await createITProject(projectForm)
      setShowProjectModal(false)
      setProjectForm({ title: '', description: '' })
      setNotificationMessage('Заявка на проект отправлена!')
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    } catch (err) {
      console.error('Ошибка запроса проекта:', err)
      setNotificationMessage(`Ошибка: ${err.message}`)
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    }
  }

  const applyFilters = () => {
    setCurrentPage(1)
    setFilters(prev => ({ ...prev, search: searchInputValue }))
  }

  const getServiceIcon = (category) => {
    switch (category) {
      case 'development': return <ComputerIcon size={20} />
      case 'design': return <SparkleIcon size={20} />
      case 'consulting': return <PeopleIcon size={20} />
      case 'maintenance': return <ConstructionIcon size={20} />
      case 'security': return <ShieldIcon size={20} />
      case 'cloud': return <ComputerIcon size={20} />
      default: return <SettingsIcon size={20} />
    }
  }

  const getCategoryName = (category) => {
    switch (category) {
      case 'development': return 'Разработка'
      case 'design': return 'Дизайн'
      case 'consulting': return 'Консалтинг'
      case 'maintenance': return 'Поддержка'
      case 'security': return 'Безопасность'
      case 'cloud': return 'Облачные решения'
      default: return 'Другое'
    }
  }

  if (loading && (services.length === 0 && projects.length === 0)) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка данных...</p>
      </div>
    )
  }

  return (
    <div className={styles.itPage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <span className={styles.icon}><ComputerIcon size={32} /></span>
            NCORE IT Услуги и Разработка
          </h1>
          <p className={styles.pageSubtitle}>
            Полный спектр IT-услуг: от разработки до технической поддержки
          </p>
        </div>
      )}

      {/* Статистика компании */}
      {/* <div className={styles.statsSection}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><StarIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>150+</div>
              <div className={styles.statLabel}>Проектов выполнено</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><StarIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>50+</div>
              <div className={styles.statLabel}>Довольных клиентов</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><CheckIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>5</div>
              <div className={styles.statLabel}>Лет опыта</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><PeopleIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>15</div>
              <div className={styles.statLabel}>Специалистов</div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Навигация */}
      <div className={styles.navigation}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${view === 'services' ? styles.active : ''}`}
            onClick={() => setView('services')}
          >
            Услуги
          </button>
          {/* <button 
            className={`${styles.tab} ${view === 'projects' ? styles.active : ''}`}
            onClick={() => setView('projects')}
          >
            Портфолио
          </button> */}
          <button 
            className={`${styles.tab} ${view === 'consultations' ? styles.active : ''}`}
            onClick={() => setView('consultations')}
          >
            Консультации
          </button>
          <button 
            className={`${styles.tab} ${view === 'about' ? styles.active : ''}`}
            onClick={() => setView('about')}
          >
            О нас
          </button>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.consultationButton}
            onClick={() => setShowConsultationModal(true)}
          >
            <PeopleIcon size={16} />
            Консультация
          </button>
          {/* <button 
            className={styles.projectButton}
            onClick={() => setShowProjectModal(true)}
          >
            <PlusIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Заказать проект
          </button> */}
          <button 
            className={styles.contactButton}
            onClick={() => setShowContactModal(true)}
          >
            Связаться с нами
          </button>
        </div>
      </div>

      {/* Поиск */}
      <div className={styles.filtersSection}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder={`Поиск ${view === 'services' ? 'по услугам' : view === 'projects' ? 'по проектам' : ''}...`}
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            className={styles.searchInput}
          />
          <button 
            onClick={applyFilters}
            className={styles.searchButton}
          >
            <SearchIcon size={16} />
            Найти
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}><ComputerIcon size={64} /></div>
          <div className={styles.errorContent}>
            <h3 className={styles.errorTitle}>Ошибка загрузки</h3>
            <p className={styles.errorMessage}>{error}</p>
            <button 
              onClick={handleRetry}
              className={styles.retryButton}
              disabled={loading}
            >
              {loading ? 'Загрузка...' : 'Попробовать снова'}
            </button>
          </div>
        </div>
      )}

      {/* Контент */}
      <div className={styles.content}>
        {view === 'services' && (
          <div className={styles.servicesGrid}>
            {services.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}><ComputerIcon size={64} /></div>
                <h3>Услуги временно недоступны</h3>
                <p>Мы работаем над обновлением каталога услуг</p>
              </div>
            ) : (
              services.map(service => (
                <div key={service.id} className={styles.serviceCard}>
                  <div className={styles.serviceHeader}>
                    {/* <div className={styles.serviceIcon}>{getServiceIcon(service.category)}</div> */}
                    <div className={styles.categoryBadge}>{getCategoryName(service.category)}</div>
                  </div>
                  <h3>{service.title || service.name}</h3>
                  <p className={styles.serviceDescription}>{service.description}</p>
                  <div className={styles.serviceFooter}>
                    <div className={styles.servicePrice}>
                      {service.price ? `${service.price} сом` : 'Цена по запросу'}
                    </div>
                    <button 
                      className={styles.orderButton} 
                      onClick={() => handleServiceOrder(service)}
                    >
                      Заказать услугу
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {view === 'projects' && (
          <div className={styles.projectsGrid}>
            {projects.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>💼</div>
                <h3>Портфолио обновляется</h3>
                <p>Скоро здесь появятся наши лучшие проекты</p>
              </div>
            ) : (
              projects.map(project => (
                <div key={project.id} className={styles.projectCard}>
                  <h3>{project.title || project.name}</h3>
                  <p>{project.description}</p>
                  <p>Status: {project.status}</p>
                  <button 
                    className={styles.projectButton} 
                    onClick={() => setSelectedProject(project)}
                  >
                    View Project
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {view === 'consultations' && (
          <div className={styles.consultationsSection}>
            <div className={styles.consultationTypes}>
              <div className={styles.consultationType}>
                <div className={styles.typeHeader}>
                  <h3>📞 Техническая консультация</h3>
                  <span className={styles.price}>Бесплатно</span>
                </div>
                <p>Получите бесплатную консультацию по техническим вопросам</p>
                <ul>
                  <li>Анализ требований</li>
                  <li>Выбор технологий</li>
                  <li>Архитектурные решения</li>
                  <li>Оценка проекта</li>
                </ul>
                <button 
                  className={styles.orderButton}
                  onClick={() => {
                    setConsultationType('technical')
                    setShowConsultationModal(true)
                  }}
                >
                  Записаться
                </button>
              </div>

              <div className={styles.consultationType}>
                <div className={styles.typeHeader}>
                  <h3>
                    <SparkleIcon size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Экспертная консультация
                  </h3>
                  <span className={styles.price}>от 5 000 Сом</span>
                </div>
                <p>Глубокий анализ с экспертом и детальными рекомендациями</p>
                <ul>
                  <li>Аудит текущих решений</li>
                  <li>Стратегия развития</li>
                  <li>Подробный отчет</li>
                  <li>План внедрения</li>
                </ul>
                <button 
                  className={styles.orderButton}
                  onClick={() => {
                    setConsultationType('expert')
                    setShowConsultationModal(true)
                  }}
                >
                  Заказать
                </button>
              </div>

              <div className={styles.consultationType}>
                <div className={styles.typeHeader}>
                  <h3>🚀 Бизнес-консультация</h3>
                  <span className={styles.price}>от 15 000 Сом</span>
                </div>
                <p>Комплексная консультация по цифровой трансформации</p>
                <ul>
                  <li>Анализ бизнес-процессов</li>
                  <li>IT-стратегия</li>
                  <li>ROI расчеты</li>
                  <li>Roadmap проекта</li>
                </ul>
                <button 
                  className={styles.orderButton}
                  onClick={() => {
                    setConsultationType('business')
                    setShowConsultationModal(true)
                  }}
                >
                  Заказать
                </button>
              </div>
            </div>
          </div>
        )}

        {view === 'about' && (
          <div className={styles.aboutSection}>

            {/* Hero-блок */}
            <div className={styles.aboutHero}>
              <div className={styles.aboutHeroBadge}>NCORE IT</div>
              <h2 className={styles.aboutHeroTitle}>
                Технологии,<br />которые работают на вас
              </h2>
              <p className={styles.aboutHeroSub}>
                Мы строим цифровые продукты с акцентом на удобство, стабильность и результат. 
                Наша миссия — сделать технологии доступными и эффективными для каждого бизнеса.
              </p>
            </div>

            {/* Наши цели */}
            <div className={styles.aboutGoals}>
              <h3 className={styles.aboutSectionTitle}>
                <SparkleIcon size={20} />
                Наши цели
              </h3>
              <div className={styles.goalsGrid}>
                <div className={styles.goalCard}>
                  <div className={styles.goalIcon}>🎯</div>
                  <h4>Лучший UX/UI дизайн</h4>
                  <p>Создаём интерфейсы, которыми приятно пользоваться — красиво, логично, без лишнего</p>
                </div>
                <div className={styles.goalCard}>
                  <div className={styles.goalIcon}>⚡</div>
                  <h4>Стабильность и производительность</h4>
                  <p>Наши решения работают без сбоев под любой нагрузкой — 24/7, 365 дней в году</p>
                </div>
                <div className={styles.goalCard}>
                  <div className={styles.goalIcon}>🔒</div>
                  <h4>Безопасность данных</h4>
                  <p>Современное шифрование, защита от атак, соблюдение стандартов безопасности</p>
                </div>
                <div className={styles.goalCard}>
                  <div className={styles.goalIcon}>🚀</div>
                  <h4>Быстрый результат</h4>
                  <p>Чёткие сроки, agile-разработка и регулярные демо — вы всегда в курсе прогресса</p>
                </div>
              </div>
            </div>

            {/* Что мы делаем */}
            <div className={styles.aboutServices}>
              <h3 className={styles.aboutSectionTitle}>
                <ComputerIcon size={20} />
                Что мы создаём
              </h3>
              <div className={styles.servicesList}>
                {[
                  { icon: '🌐', label: 'Веб-приложения', desc: 'SPA, многостраничные порталы, дашборды' },
                  { icon: '🖥️', label: 'Корпоративные сайты', desc: 'Презентационные и продающие сайты' },
                  { icon: '📇', label: 'Визитки и лендинги', desc: 'Быстро, стильно, конверсионно' },
                  { icon: '🎓', label: 'Онлайн-школы', desc: 'LMS-платформы, курсы, тесты, прогресс' },
                  { icon: '🗂️', label: 'Личные кабинеты', desc: 'Кабинеты клиентов, сотрудников, партнёров' },
                  { icon: '📊', label: 'CRM-системы', desc: 'Управление клиентами, сделками, задачами' },
                  { icon: '💬', label: 'Корпоративные мессенджеры', desc: 'Чаты, каналы, видеозвонки, файлы' },
                  { icon: '🛒', label: 'Интернет-магазины', desc: 'Каталоги, корзины, оплата, доставка' },
                  { icon: '📱', label: 'Мобильные приложения', desc: 'React Native, iOS и Android' },
                  { icon: '�', label: 'Системы задач и проектов', desc: 'Трекеры задач, kanban, спринты, отчёты' },
                  { icon: '📄', label: 'Документооборот (ЭДО)', desc: 'Согласование, подписи, архив документов' },
                  { icon: '🏢', label: 'ERP-системы', desc: 'Склад, финансы, производство, снабжение' },
                  { icon: '👥', label: 'HR-порталы', desc: 'Найм, онбординг, KPI, табели, отпуска' },
                  { icon: '📆', label: 'Корпоративные календари', desc: 'Бронирование переговорок, расписания' },
                  { icon: '📡', label: 'Порталы и интранеты', desc: 'Внутренняя база знаний, новости, справочники' },
                  { icon: '�🔗', label: 'API-интеграции', desc: 'Подключение платёжных, CRM, ERP-систем' },
                  { icon: '☁️', label: 'Облачная инфраструктура', desc: 'DevOps, CI/CD, контейнеризация' },
                  { icon: '🛡️', label: 'Аудит и безопасность', desc: 'Пентесты, код-ревью, исправление уязвимостей' },
                ].map(({ icon, label, desc }) => (
                  <div key={label} className={styles.serviceItem}>
                    <span className={styles.serviceItemIcon}>{icon}</span>
                    <div>
                      <div className={styles.serviceItemLabel}>{label}</div>
                      <div className={styles.serviceItemDesc}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Почему мы */}
            <div className={styles.aboutWhy}>
              <h3 className={styles.aboutSectionTitle}>
                <CheckIcon size={20} />
                Почему выбирают нас
              </h3>
              <div className={styles.whyGrid}>
                <div className={styles.whyItem}>
                  <div className={styles.whyNum}>01</div>
                  <h4>Индивидуальный подход</h4>
                  <p>Разрабатываем решение под ваш бизнес, а не подгоняем вас под шаблон</p>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyNum}>02</div>
                  <h4>Прозрачные цены</h4>
                  <p>Фиксированная стоимость, никаких скрытых платежей и сюрпризов после сдачи</p>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyNum}>03</div>
                  <h4>Поддержка после запуска</h4>
                  <p>Не бросаем проект после деплоя — сопровождаем, дорабатываем, масштабируем</p>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyNum}>04</div>
                  <h4>Современный стек</h4>
                  <p>React, Django, Node.js, PostgreSQL, Redis, Docker — только проверенные технологии</p>
                </div>
              </div>
            </div>

            {/* Форма консультации */}
            <div className={styles.aboutConsultForm}>
              <div className={styles.aboutConsultFormLeft}>
                <div className={styles.aboutConsultFormBadge}>Бесплатно</div>
                <h3>Готовы обсудить ваш проект?</h3>
                <p>Расскажите нам о своей задаче — мы проанализируем её и предложим лучшее решение. Без обязательств.</p>
                <ul className={styles.aboutConsultPerks}>
                  <li>Ответим в течение 2 часов</li>
                  <li>Оценим бюджет и сроки</li>
                  <li>Предложим технологический стек</li>
                </ul>
              </div>
              <div className={styles.aboutConsultFormRight}>
                <form onSubmit={handleConsultationRequest} className={styles.aboutForm}>
                  <div className={styles.formGroup}>
                    <label>Ваше имя</label>
                    <input
                      type="text"
                      value={consultationForm.name}
                      onChange={(e) => setConsultationForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={user ? user.username : 'Иван Иванов'}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Email</label>
                    <input
                      type="email"
                      value={consultationForm.email}
                      onChange={(e) => setConsultationForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={user ? user.email : 'ivan@example.com'}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Телефон</label>
                    <input
                      type="tel"
                      value={consultationForm.phone}
                      onChange={(e) => setConsultationForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder={user && user.phone ? user.phone : '+996 700 000 000'}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Опишите ваш проект или задачу</label>
                    <textarea
                      value={consultationForm.description}
                      onChange={(e) => setConsultationForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Например: нужен сайт-лендинг для онлайн-школы с оплатой и личным кабинетом..."
                      required
                    />
                  </div>
                  <button type="submit" className={styles.aboutFormSubmit}>
                    <PeopleIcon size={16} />
                    Отправить заявку
                  </button>
                </form>
              </div>
            </div>

          </div>
        )}

        {loading && (
          <div className={styles.loadingMore}>
            <Loader size="medium" />
          </div>
        )}

        {totalPages > 1 && (view === 'services' || view === 'projects') && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Модальные окна */}
      {showConsultationModal && (
        <div className={styles.consultationModal} data-lenis-prevent>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>IT Консультация {consultationType === 'technical' ? '(Техническая)' : consultationType === 'expert' ? '(Экспертная)' : '(Бизнес)'}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => {
                  setShowConsultationModal(false)
                  setConsultationType('')
                }}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Запишитесь на консультацию с нашими IT специалистами.</p>
              <form onSubmit={handleConsultationRequest} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Ваше имя</label>
                  <input 
                    type="text" 
                    value={consultationForm.name}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={user ? user.username : ''}
                    required 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={consultationForm.email}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={user ? user.email : ''}
                    required 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Телефон</label>
                  <input 
                    type="tel" 
                    value={consultationForm.phone}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder={user && user.phone ? user.phone : '+996 700 000 000'}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Опишите ваши IT потребности</label>
                  <textarea 
                    value={consultationForm.description}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, description: e.target.value }))}
                    required
                  ></textarea>
                </div>
                <button type="submit" className={styles.submitButton}>Отправить заявку</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showProjectModal && (
        <div className={styles.projectModal} data-lenis-prevent>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Заказать проект</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowProjectModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Закажите разработку индивидуального IT проекта.</p>
              <form onSubmit={handleProjectRequest} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Название проекта</label>
                  <input 
                    type="text" 
                    value={projectForm.title}
                    onChange={(e) => setProjectForm(prev => ({ ...prev, title: e.target.value }))}
                    required 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Описание проекта</label>
                  <textarea 
                    value={projectForm.description}
                    onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
                    required
                  ></textarea>
                </div>
                <button type="submit" className={styles.submitButton}>Отправить заявку</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className={styles.contactModal} data-lenis-prevent>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Связаться с нами</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowContactModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Свяжитесь с нашей IT командой.</p>
              <p>Email: ncore@gmail.com</p>
              <p>Телефон: +996600006032</p>
            </div>
          </div>
        </div>
      )}

      {selectedProject && (
        <div className={styles.projectViewModal} data-lenis-prevent>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{selectedProject.title || selectedProject.name}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setSelectedProject(null)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>{selectedProject.description}</p>
              <p>Status: {selectedProject.status}</p>
              <p>Technologies: {selectedProject.technologies}</p>
            </div>
          </div>
        </div>
      )}

      {/* Service Request Modal */}
      {showServiceModal && selectedService && (
        <ServiceRequestModal
          isOpen={showServiceModal}
          service={selectedService}
          user={user}
          onClose={handleServiceRequestClose}
          onSubmit={async (requestData) => {
            try {
              const { createServiceRequest } = await import('../../../service/ITService')
              await createServiceRequest(selectedService.slug || selectedService.id, requestData)
              handleServiceRequestSuccess('Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.')
            } catch (error) {
              console.error('Error creating service request:', error)
              throw error
            }
          }}
        />
      )}

      {/* Auth Notification */}
      {showAuthNotification && (
        <div className={styles.authNotification}>
          <div className={styles.notificationContent}>
            <span className={styles.notificationIcon}><CheckIcon size={20} /></span>
            <span className={styles.notificationMessage}>{notificationMessage}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ITPage